import React, { ReactNode, useEffect, useState, useRef } from 'react'
import styled from 'styled-components';
import ChevronDownIcon from '../../../assets/icons/ChevronDownIcon';
import { useTheme } from '../../../middleware/ThemeContext';
import CustomFieldsIcon from '../../../assets/icons/CustomFieldsIcon';
import StyledText from '../../../components/StyledComponents/StyledText';
import { formatLabel, formatDateDefault } from '../../../utils/helper';
import { useLazyGetFileQuery } from '../../../api/searchQueryApiSlice';
import FileUpload from '../../../components/form/ProfileImageUpload';
import CustomInput from '../../../components/form/CustomInput';
import { ErrorText } from '../../../components/FormBuilder/FieldSettings/FieldSettingsModal';
import CustomTextArea from '../../../components/form/CustomTextArea';
import CalendarIcon from '../../../assets/icons/CalendarIcon';
import AMSSelect from '../../../components/AMSSelect';
import { CheckboxItem } from '../../../components/listing/ColumnIdentifier';
import EditIcon from '../../../assets/icons/EditIcon';
import CrossIcon from '../../../assets/icons/CrossIcon';
import TickIcon from '../../../assets/icons/TickIcon';
import LoadingCircleIcon from '../../../assets/icons/LoadingCircleIcon';
import { useUpdateContactMutation } from '../../../api/contactApiSlice';
import { useToast } from '../../../components/toast/ToastContext';
import { useUpdateOrganizationMutation } from '../../../api/organizationApiSlice';
import { Theme } from '../../../utils/themes';

interface Accordion {
    ID: number;
    Email: string;
    icon?: ReactNode;
    data: any;
    type: string;
    isOpen: boolean;
    searchTerm?: string;
    refetch?: () => void;
    scope: 'CONTACT' | 'ORGANIZATION';
}

function formatValue(value: any): string {
    try {
        // Check if the value looks like a JSON array
        if (typeof value === "string" && value.startsWith("[") && value.endsWith("]")) {
            const parsed = JSON.parse(value);

            // If parsed is an array, join with commas
            if (Array.isArray(parsed)) {
                return parsed.join(", ");
            }
        }

        // Otherwise, just return as-is
        return String(value);
    } catch {
        return String(value);
    }
}

const FieldsGroupAccordion: React.FC<Accordion> = ({ ID, Email, icon, data, type, isOpen, searchTerm, refetch, scope }) => {
    const [open, setOpen] = useState(isOpen);
    const { theme } = useTheme();
    const showToast = useToast();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [fieldValue, setFieldValue] = useState<any>(null);
    const [getFile, { data: filePath, isLoading }] = useLazyGetFileQuery();
    // cache preview urls per file key to avoid refetching on every render
    const previewCacheRef = useRef<Map<string, string | null>>(new Map());

    // Small helper component to fetch and preview a file (image/video/audio)
    const FilePreviewer: React.FC<{ fileKey: string | undefined | null }> = ({ fileKey }) => {
        const [previewUrl, setPreviewUrl] = useState<string | null>(null);
        const [loadingPreview, setLoadingPreview] = useState(false);

        useEffect(() => {
            let objectUrl: string | null = null;

            // if no fileKey, clear
            if (!fileKey) {
                setPreviewUrl(null);
                return;
            }

            // check cache first
            const cached = previewCacheRef.current.get(fileKey);
            if (cached !== undefined) {
                setPreviewUrl(cached);
                return;
            }

            const fetchPreview = async () => {
                try {
                    setLoadingPreview(true);
                    const result = await getFile(fileKey).unwrap();

                    // API may return different shapes: { url: string } or { response: string } or a Blob
                    if (result instanceof Blob) {
                        objectUrl = window.URL.createObjectURL(result);
                        previewCacheRef.current.set(fileKey, objectUrl);
                        setPreviewUrl(objectUrl);
                    } else {
                        // handle object shapes
                        const url = (result && (result.url || result.response || result.signedUrl || result.data)) || null;
                        if (url) {
                            previewCacheRef.current.set(fileKey, url);
                            setPreviewUrl(url);
                        } else {
                            previewCacheRef.current.set(fileKey, null);
                            setPreviewUrl(null);
                        }
                    }
                } catch (err) {
                    console.error('Preview fetch failed', err);
                    previewCacheRef.current.set(fileKey, null);
                    setPreviewUrl(null);
                } finally {
                    setLoadingPreview(false);
                }
            };

            fetchPreview();

            return () => {
                if (objectUrl) {
                    window.URL.revokeObjectURL(objectUrl);
                }
            };
        }, [fileKey]);

        if (loadingPreview) return <div>Loading preview...</div>;
        if (!previewUrl) return <div>No preview available</div>;

        const lower = previewUrl.split('?')[0].toLowerCase();
        if (lower.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
            return <img src={previewUrl} alt="preview" style={{ maxWidth: '200px', maxHeight: '160px', borderRadius: 4, objectFit: 'cover' }} />;
        }
        if (lower.match(/\.(mp4|webm|mov|m4v|ogg|ogv)$/)) {
            return <video controls src={previewUrl} style={{ maxWidth: '320px', maxHeight: '240px', borderRadius: 4 }} />;
        }
        if (lower.match(/\.(mp3|wav|m4a|aac|ogg)$/)) {
            return <audio controls src={previewUrl} />;
        }

        return (
            <div>
                <a href={previewUrl} target="_blank" rel="noreferrer">Open file</a>
            </div>
        );
    };

    // Preview a media URL (YouTube/Vimeo or direct audio/video links)
    const MediaURLPreview: React.FC<{ url?: string | null }> = ({ url }) => {
        if (!url) return <div>No preview available</div>;

        const getYouTubeVideoId = (u: string) => {
            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
            const match = u.match(regExp);
            return (match && match[7].length === 11) ? match[7] : null;
        };

        const getVimeoVideoId = (u: string) => {
            const regExp = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/;
            const match = u.match(regExp);
            return match ? match[1] : null;
        };

        const lower = url.split('?')[0].toLowerCase();

        const youTubeId = getYouTubeVideoId(url);
        const vimeoId = getVimeoVideoId(url);

        if (youTubeId) {
            return (
                <iframe
                    width="100%"
                    height="315"
                    src={`https://www.youtube.com/embed/${youTubeId}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            );
        }

        if (vimeoId) {
            return (
                <iframe
                    src={`https://player.vimeo.com/video/${vimeoId}`}
                    width="100%"
                    height="315"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                />
            );
        }

        if (lower.match(/\.(mp4|webm|mov|m4v|ogg|ogv)$/)) {
            return <video controls src={url} style={{ maxWidth: '100%', maxHeight: '360px', borderRadius: 4 }} />;
        }

        if (lower.match(/\.(mp3|wav|m4a|aac|ogg)$/)) {
            return <audio controls src={url} />;
        }

        return (
            <div>
                <a href={url} target="_blank" rel="noreferrer">Open link</a>
            </div>
        );
    };
    const [updateFields, { isLoading: loadEdit }] = useUpdateContactMutation();
    // const [updateOrganizations, {isLoading: loadEditOrg}] = useMergeOrganizationsMutation();
    const [updateOrganizations, { isLoading: loadEditOrg }] = useUpdateOrganizationMutation();

    const onUpdate = async () => {
        const value = {
            id: ID,
            // contactMeta: {
            //     email: Email,
            // },
            fields: [{
                fieldId: editingIndex,
                value: type === 'MULTIPLE_CHOICES' ? fieldValue.join(",") : String(fieldValue),
            }]
        }
        try {
            if (scope === 'CONTACT') {
                await updateFields(value).unwrap();
            } else if (scope === 'ORGANIZATION') {
                await updateOrganizations(value).unwrap();
            }
            showToast("Successfully", "Field updated successfully", "success");
            setEditingIndex(null);
            refetch && refetch();
        } catch (error: any) {
            showToast(
                "Error",
                error?.data?.message || "Failed to update field",
                "error"
            );
        }

    }

    const renderValueField = (
        fieldKey: string,
        index: number,
        setFieldValue: (value: any) => void,
        currentValue: any,
        error?: string | undefined
    ) => {
        const selectedField = data.find((field: any) => field.field.id === fieldKey);
        if (!selectedField) return null;

        const commonProps = {
            value: currentValue || "",
            onChange: (value: any) => setFieldValue(value),
            hasError: !!error,
        };

        switch ((selectedField.field.dataType)?.toUpperCase()) {
            case "SINGLE_LINE":
                return (
                    <FieldContainer>
                        <ModifiedCustomInput
                            theme={theme}
                            className='w-100'
                            {...commonProps}
                            placeholder={`Enter ${selectedField.field.name}`}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "MULTI_LINE":
                return (
                    <FieldContainer>
                        <CustomTextArea
                            {...commonProps}
                            placeholder={`Enter ${selectedField.field.name}`}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "NUMBERS":
                return (
                    <FieldContainer>
                        <ModifiedCustomInput
                            theme={theme}
                            {...commonProps}
                            type="number"
                            placeholder={`Enter ${selectedField.field.name}`}
                            step="1"
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "DECIMAL":
            case "CURRENCY":
                return (
                    <FieldContainer>
                        <ModifiedCustomInput
                            theme={theme}
                            {...commonProps}
                            type="number"
                            placeholder={`Enter ${selectedField.field.name}`}
                            step={((): string => {
                                const ui = (selectedField.field as any)?.config?.uiMeta || {};
                                const allowDecimal = !!ui.allowDecimal || ((selectedField.field.dataType || '').toUpperCase() === 'DECIMAL');
                                const dp = ui.decimalPlaces as number | undefined;
                                if (!allowDecimal) return '1';
                                if (dp === undefined) return 'any';
                                if (dp === 0) return '1';
                                return '0.' + '0'.repeat(Math.max(0, dp - 1)) + '1';
                            })()}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "DATE_TIME":
            case "DATE":
                return (
                    <FieldContainer>
                        <CustomInput
                            type="date"
                            rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                            placeholder={`Select ${selectedField.field.name}`}
                            value={currentValue || ""}
                            onChange={(value: any) => setFieldValue(value)}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "SELECT":
                return (
                    <FieldContainer>
                        <AMSSelect
                            staticOptions={(selectedField.field.config.options || []).map((opt: any) => ({
                                label: opt,
                                value: opt,
                            }))}
                            value={(() => {
                                const options = (selectedField.field.config.options || []).map((opt: any) => ({
                                    label: opt,
                                    value: opt,
                                }));
                                return options.find((o: any) => String(o.value) === String(currentValue)) ?? null;
                            })()}
                            onChange={(opt: any) => setFieldValue(opt?.value ?? '')}
                            placeholder={`Select ${selectedField.field.name}`}
                            menuPosition="fixed"
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "RADIO":
                return (
                    <FieldContainer>
                        <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                            {selectedField.field.config.options?.map((opt: any, idx: number) => (
                                <CheckboxItem theme={theme} key={idx}>
                                    <input
                                        type="radio"
                                        name={`radio-${fieldKey}-${index}`}
                                        value={opt}
                                        checked={currentValue === opt}
                                        onChange={(e) => {
                                            const newValue = e.target.checked ? opt : "";
                                            setFieldValue(newValue);
                                        }}
                                    />
                                    <span>{opt}</span>
                                </CheckboxItem>
                            ))}
                        </div>
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );
            case "BOOLEAN":
            case "CHECKBOX":
                return (
                    <FieldContainer>
                        <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                            <CheckboxItem theme={theme} key={selectedField.key}>
                                <input
                                    type="checkbox"
                                    checked={currentValue === true || currentValue === "true"}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setFieldValue(newValue);
                                    }}
                                />
                                <span>{selectedField?.fieldLabel || selectedField.field.name}</span>
                            </CheckboxItem>
                        </div>
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "MULTIPLE_CHOICES":
                return (
                    <FieldContainer>
                        <AMSSelect
                            staticOptions={(selectedField.field.config.options || []).map((opt: any) => ({
                                label: opt,
                                value: opt,
                            }))}
                            isMulti
                            value={(() => {
                                const selectedValues = Array.isArray(currentValue) ? currentValue : [];
                                const options = (selectedField.field.config.options || []).map((opt: any) => ({
                                    label: opt,
                                    value: opt,
                                }));
                                return options.filter((o: any) => selectedValues.some((v: any) => String(v) === String(o.value)));
                            })()}
                            onChange={(opts: any) => {
                                const selected = Array.isArray(opts) ? opts : [];
                                setFieldValue(selected.map((o: any) => o?.value));
                            }}
                            placeholder="Select field scope"
                            menuPosition="fixed"
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "VIDEO_EMBED":
            case "AUDIO_EMBED":
                return (
                    <FieldContainer>
                        <ModifiedCustomInput
                            theme={theme}
                            {...commonProps}
                            placeholder={`Enter ${selectedField.field.name} URL`}
                            type="url"
                        />
                        <div style={{ marginTop: 8 }}>
                            <MediaURLPreview url={currentValue} />
                        </div>
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            case "FILE_UPLOAD":
            case "IMAGE_UPLOAD":
                return (
                    <FieldContainer>
                        {/* Preview existing file */}
                        {currentValue ? (
                            <div style={{ marginBottom: 8 }}>
                                <FilePreviewer fileKey={currentValue} />
                            </div>
                        ) : null}

                        {/* Upload control to replace file */}
                        <div>
                            <FileUpload
                                viewImageThumbnail={false}
                                folder={scope === 'CONTACT' ? 'contact' : 'organization'}
                                onUploadSuccess={(key) => {
                                    // when upload succeeds, set the new file key into the edit state
                                    setFieldValue(key);
                                }}
                                onUploadError={(err) => showToast('Upload Error', String(err), 'error')}
                            />
                        </div>

                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );

            default:
                return (
                    <FieldContainer>
                        <ModifiedCustomInput
                            theme={theme}
                            {...commonProps}
                            placeholder={`Enter ${selectedField.field.name}`}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FieldContainer>
                );
        }
    };

    const handleClick = async (file: string) => {
        try {
            const result = await getFile(file).unwrap();

            if (result?.url) {
                // case 1: API returns a URL
                const link = document.createElement("a");
                link.href = result.url;
                link.download = file; // optional: you can set filename
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else if (result instanceof Blob) {
                // case 2: API returns binary blob
                const blobUrl = window.URL.createObjectURL(result);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = file;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    useEffect(() => {
        if (searchTerm && searchTerm.trim() !== '') {
            setOpen(true);
        } else {
            setOpen(isOpen);
        }
    }, [searchTerm, isOpen]);

    const getFilterData = (theType: string) => {
        return {
            fieldValues: data?.filter((item: any) => item?.field?.dataType === theType) || [],
        };
    };



    const filterData = getFilterData(type);

    return (
        filterData?.fieldValues?.length > 0 ? (
            <AccordionCon theme={theme}>
                <div
                    className="d-flex align-items-center justify-content-between p-3 cursor-pointer"
                    style={{
                        backgroundColor: theme.CorporateTheme?.colors?.filterBG,
                    }}
                    onClick={() => setOpen(!open)}
                >
                    <StyledText className="d-flex align-items-center gap-1" fontSize='14px' fontWeight={600} style={{ textTransform: 'capitalize' }}>
                        {icon ? icon : null}
                        {type ? formatLabel(type).toLowerCase() : 'Custom Fields'}{" "}
                        <span style={{
                            color: `${theme.CorporateTheme.colors.count}`,
                            fontFamily: 'Lato',
                        }}>({filterData?.fieldValues?.length ?? 0})</span>
                    </StyledText>
                    <ChevronDownIcon
                        color={theme.CorporateTheme.colors.textSecondary}
                        style={{ rotate: open ? '180deg' : '' }}
                    />
                </div>

                {open && (
                    <div className="data px-3 py-2">
                        {filterData.fieldValues.map((val: any, ind: number) => (
                            <div className="field d-flex justify-content-between" key={ind}>
                                {editingIndex !== val.field.id ? (
                                    <>
                                        <div className='d-flex align-items-baseline gap-1 col-11'>
                                            {/* <div style={{ width: '20px' }}>
                                                <CustomFieldsIcon color={theme.colors.topBarsColorText} />
                                            </div> */}
                                            <div>
                                                {icon}
                                            </div>
                                            <div style={{ overflow: 'hidden' }}>
                                                <StyledText
                                                    textColor={theme.CorporateTheme.colors.headerColor}
                                                    fontSize="13px"
                                                    fontWeight={500}
                                                    className="mb-2"
                                                    style={{ overflow: 'hidden', textTransform: 'capitalize' }}
                                                >
                                                    {val?.field?.name}
                                                </StyledText>
                                                {type === 'FILE_UPLOAD' || type === 'IMAGE_UPLOAD' ? (
                                                    <div style={{ maxWidth: 320 }}>
                                                        <FilePreviewer fileKey={val?.value} />
                                                    </div>
                                                ) : type === 'VIDEO_EMBED' || type === 'AUDIO_EMBED' ? (
                                                    <div style={{ maxWidth: 320 }}>
                                                        <MediaURLPreview url={val?.value} />
                                                    </div>
                                                ) : type === 'CHECKBOX' ? (
                                                    <div>
                                                        <CheckboxItem theme={theme}>
                                                            <input
                                                                type="checkbox"
                                                                checked={val?.value === true || val?.value === "true"}
                                                            />
                                                        </CheckboxItem>
                                                    </div>
                                                ) : (
                                                    <StyledText
                                                        textColor={theme.CorporateTheme.colors.primary}
                                                        fontSize="14px"
                                                        fontWeight={600}
                                                    >
                                                        {val?.field?.dataType === 'DATE' || val?.field?.dataType === 'DATE_TIME'
                                                            ? formatDateDefault(val?.value)
                                                            : val?.field?.dataType === 'MULTIPLE_CHOICES'
                                                                ? formatValue(val?.value)
                                                                : val?.value}
                                                    </StyledText>
                                                )}
                                            </div>
                                        </div>
                                        {type === 'FILE_UPLOAD' ? (
                                            <EditIcon
                                                className='cursor-pointer'
                                                color={theme.CorporateTheme.colors.gunmetal}
                                                onClick={() => { setEditingIndex(val.field.id); setFieldValue('') }} />
                                        )
                                            : type === 'MULTIPLE_CHOICES' ? (
                                                <EditIcon
                                                    className='cursor-pointer'
                                                    color={theme.CorporateTheme.colors.gunmetal}
                                                    // onClick={() => { setEditingIndex(val.field.id); setFieldValue(formatValue(val.value.split(","))) }} 
                                                    onClick={() => {
                                                        let parsedValue = [];
                                                        if (typeof val.value === 'string') {
                                                            if (val.value.startsWith('[') && val.value.endsWith(']')) {
                                                                try {
                                                                    parsedValue = JSON.parse(val.value);
                                                                } catch {
                                                                    parsedValue = val.value ? val.value.split(',').map((s: string) => s.trim()) : [];
                                                                }
                                                            } else {
                                                                parsedValue = val.value ? val.value.split(',').map((s: string) => s.trim()) : [];
                                                            }
                                                        } else if (Array.isArray(val.value)) {
                                                            parsedValue = val.value;
                                                        }
                                                        setEditingIndex(val.field.id);
                                                        setFieldValue(parsedValue); // ← must be an array!
                                                    }}
                                                />
                                            ) :
                                                (
                                                    <EditIcon
                                                        className='cursor-pointer'
                                                        color={theme.CorporateTheme.colors.gunmetal}
                                                        onClick={() => { setEditingIndex(val.field.id); setFieldValue(val.value) }} />
                                                )}
                                    </>
                                ) : (
                                    <>
                                        <div className='w-100 me-3'>
                                            {
                                                renderValueField(
                                                    val.field.id,
                                                    ind,
                                                    setFieldValue,
                                                    fieldValue
                                                )
                                            }
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <CrossIcon
                                                className={`${loadEdit && loadEditOrg && 'class-disable'} cursor-pointer`}
                                                onClick={() => setEditingIndex(null)}
                                                color={theme.CorporateTheme.colors.gunmetal}
                                            />
                                            {loadEdit || loadEditOrg ? (
                                                <LoadingCircleIcon color={theme.CorporateTheme.colors.secondary} />
                                            ) : (
                                                <TickIcon
                                                    className="cursor-pointer"
                                                    onClick={onUpdate}
                                                    color={theme.CorporateTheme.colors.gunmetal}
                                                />
                                            )}

                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </AccordionCon>
        ) : null
    );
};

const AccordionCon = styled.div`
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    border-radius: 5px;
    margin-bottom: 10px;

    & .data .field {
        padding: 10px 0px;
    }
    & .data .field:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    }
`;

const ModifiedCustomInput = styled(CustomInput) <{ theme: Theme }>`
   background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
   border:none;
`;

const FieldContainer = styled.div`
  position: relative;
  min-height: 42px; /* Fixed height for input fields */
  min-width: 150px;
`;
export default FieldsGroupAccordion;